import { useState } from "react";
import { Sparkles, X, Copy, Check } from "lucide-react";
import { streamSSE } from "@/lib/sse";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KINDS = [
    { id: "post", label: "منشور تواصل اجتماعي" },
    { id: "service", label: "وصف خدمة" },
    { id: "whatsapp", label: "رسالة واتساب للعملاء" },
];

const ContentStudio = ({ open, onClose }) => {
    const [kind, setKind] = useState("post");
    const [topic, setTopic] = useState("");
    const [output, setOutput] = useState("");
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);

    if (!open) return null;

    const generate = async () => {
        if (!topic.trim() || busy) return;
        setBusy(true);
        setOutput("");
        setCopied(false);
        try {
            await streamSSE(`${API}/content`, { kind, topic: topic.trim() }, (delta) =>
                setOutput((o) => o + delta),
            );
        } catch {
            setOutput("عذراً، تعذر التوليد حالياً — حاول مرة أخرى بعد قليل.");
        }
        setBusy(false);
    };

    const copy = async () => {
        await navigator.clipboard.writeText(output);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    };

    return (
        <div className="fixed inset-0 z-[80] overflow-y-auto bg-[#0B0F17]/95 backdrop-blur-md" data-testid="content-studio-overlay">
            <div className="mx-auto max-w-2xl px-4 py-10 sm:py-16">
                <div data-testid="content-studio-panel" className="rounded-[2rem] border border-[#232F45] bg-[#121824] p-6 sm:p-9">
                    <div className="mb-7 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-500/15 text-amber-400">
                                <Sparkles size={22} />
                            </span>
                            <div>
                                <h2 className="font-display text-xl font-black text-white">مولّد المحتوى الذكي</h2>
                                <p className="text-xs text-gray-500">نصوص جاهزة لنشاطك — انسخ وانشر</p>
                            </div>
                        </div>
                        <button onClick={onClose} data-testid="studio-close" aria-label="إغلاق" className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 text-gray-400 transition-colors hover:text-white">
                            <X size={18} />
                        </button>
                    </div>

                    <div className="mb-5 flex flex-wrap gap-2">
                        {KINDS.map((k) => (
                            <button
                                key={k.id}
                                data-testid={`studio-kind-${k.id}`}
                                onClick={() => setKind(k.id)}
                                className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                                    kind === k.id
                                        ? "bg-amber-500 text-[#0B0F17]"
                                        : "border border-[#232F45] bg-[#151C2A] text-gray-300 hover:border-amber-500/40"
                                }`}
                            >
                                {k.label}
                            </button>
                        ))}
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row">
                        <input
                            data-testid="studio-topic-input"
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            placeholder="عن ماذا تريد أن نكتب؟ مثال: عرض برمجة مفاتيح تويوتا"
                            className="h-12 flex-1 rounded-xl border border-[#232F45] bg-[#0B0F17] px-4 text-sm text-white placeholder:text-gray-600 focus:border-amber-500 focus:outline-none"
                        />
                        <button
                            onClick={generate}
                            data-testid="studio-generate-button"
                            disabled={busy || !topic.trim()}
                            className="cta-shine flex h-12 items-center justify-center gap-2 rounded-xl bg-amber-500 px-6 text-sm font-extrabold text-[#0B0F17] transition-colors hover:bg-amber-400 disabled:opacity-50"
                        >
                            <Sparkles size={16} />
                            {busy ? "جارٍ الكتابة…" : "ولّد النص"}
                        </button>
                    </div>

                    {(output || busy) && (
                        <div className="mt-6">
                            <div
                                data-testid="studio-output"
                                className="min-h-[8rem] whitespace-pre-wrap rounded-2xl border border-white/8 bg-[#0B0F17] p-5 text-sm leading-loose text-gray-200"
                            >
                                {output || "…"}
                            </div>
                            {output && !busy && (
                                <button
                                    onClick={copy}
                                    data-testid="studio-copy-button"
                                    className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm font-bold text-emerald-400 transition-colors hover:bg-emerald-500/20"
                                >
                                    {copied ? <Check size={15} /> : <Copy size={15} />}
                                    {copied ? "تم النسخ" : "نسخ النص"}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContentStudio;
